import ExcelJS from 'exceljs';

interface ColumnDefinition<T> {
	header: string;
	key: keyof T;
	width?: number;
	format?: (value: any) => string | number;
	style?: Partial<ExcelJS.Style>;
}

interface ExportOptions<T> {
	fileName: string;
	sheetName?: string;
	columns: ColumnDefinition<T>[];
	data: T[];
	title?: string;
	headerStyle?: Partial<ExcelJS.Style>;
	freezeHeader?: boolean;
	autoFilter?: boolean;
}

export async function exportToExcel<T extends Record<string, any>>({
	fileName,
	sheetName = 'Sheet1',
	columns,
	data,
	title,
	headerStyle,
	freezeHeader = true,
	autoFilter = true,
}: ExportOptions<T>): Promise<void> {
	try {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet(sheetName);

		let startRow = 1;

		// Add title if provided
		if (title) {
			worksheet.mergeCells(1, 1, 1, columns.length);
			const titleCell = worksheet.getCell(1, 1);
			titleCell.value = title;
			titleCell.font = { size: 16, bold: true };
			titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
			startRow = 3;
			worksheet.addRow([]); // Empty row (row 2)

			// Add header row manually at row 3
			const headerRow = worksheet.getRow(startRow);
			columns.forEach((col, index) => {
				headerRow.getCell(index + 1).value = col.header;
			});
		} else {
			// No title, use standard column definition
			worksheet.columns = columns.map(col => ({
				header: col.header,
				key: col.key as string,
				width: col.width || 15,
			}));
		}

		// Set column widths (important when title exists and we manually add headers)
		columns.forEach((col, index) => {
			worksheet.getColumn(index + 1).width = col.width || 15;
			worksheet.getColumn(index + 1).key = col.key as string;
		});

		// Style header row
		const headerRow = worksheet.getRow(startRow);
		headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
		headerRow.fill = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FF4472C4' },
		};
		headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
		headerRow.height = 25;

		// Apply custom header styles if provided
		if (headerStyle) {
			Object.assign(headerRow, headerStyle);
		}

		// Add data rows
		data.forEach((item, index) => {
			const rowData: Record<string, any> = {};

			columns.forEach(col => {
				const value = item[col.key];
				rowData[col.key as string] = col.format
					? col.format(value)
					: value;
			});

			const row = worksheet.addRow(rowData);

			// Alternating row colors
			if (index % 2 === 0) {
				row.fill = {
					type: 'pattern',
					pattern: 'solid',
					fgColor: { argb: 'FFF2F2F2' },
				};
			}

			// Apply column-specific styles
			columns.forEach((col, colIndex) => {
				if (col.style) {
					Object.assign(row.getCell(colIndex + 1), col.style);
				}
			});
		});

		// Freeze header
		if (freezeHeader) {
			worksheet.views = [
				{ state: 'frozen', xSplit: 0, ySplit: startRow },
			];
		}

		// Add auto filter
		if (autoFilter) {
			worksheet.autoFilter = {
				from: { row: startRow, column: 1 },
				to: { row: startRow, column: columns.length },
			};
		}

		// Add borders to all cells
		worksheet.eachRow((row, rowNumber) => {
			if (rowNumber >= startRow) {
				row.eachCell(cell => {
					cell.border = {
						top: { style: 'thin' },
						left: { style: 'thin' },
						bottom: { style: 'thin' },
						right: { style: 'thin' },
					};
				});
			}
		});

		// Generate and download file
		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		});

		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = fileName.endsWith('.xlsx')
			? fileName
			: `${fileName}.xlsx`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(link.href);
	} catch (error) {
		console.error('Error exporting to Excel:', error);
		throw new Error('Failed to export Excel file');
	}
}
